import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { Link2, Link2Off } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { TeamMemberInvitationStatusDTO } from '@/modules/team/types/dto/team-member-invitation-status.dto';

import { EmployeesTableFormValues } from '../api/employee-schema';
import { Employee } from '../types/employees.types';

const columnHelper = createColumnHelper<Employee>();

export interface TeamColumnDeps {
  connectedIntegrations: IntegrationServices[];
  currentUserEmail?: string;
  isCurrentUserSuperAdmin?: boolean;
}

const getIntegrationColumns = (integrations: IntegrationServices[]) =>
  integrations.map(integration =>
    columnHelper.display({
      id: integration.toLowerCase(),
      header: () => (
        // Narrow integration columns: truncate the (often long) name and scroll
        // it on hover so the full label is always reachable, never cut off.
        <div className="flex w-full min-w-0 items-center justify-center">
          <ScrollableText className="text-center">{integration}</ScrollableText>
        </div>
      ),
      meta: { cellClassName: '!align-top' },
      cell: info => {
        const isConnected = info.row.original.connectedIntegrations.includes(integration);
        return (
          <div className="flex h-10 items-center justify-center">
            {isConnected ? (
              <Link2 className="h-5 w-5 text-success" />
            ) : (
              <Link2Off className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      size: 64,
      minSize: 64,
      maxSize: 150,
    })
  );

interface EmployeeColumnsParams {
  t: TFunction;
  form: ReturnType<typeof useForm<EmployeesTableFormValues>>;
  /** Resolves an entity id to its index in the FULL form array (see the reconciliation docs). */
  getFormIndex: (entityId: number) => number;
  deps?: TeamColumnDeps;
}

export const getEmployeeColumns = ({ t, form, getFormIndex, deps }: EmployeeColumnsParams) => [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <label className="flex cursor-pointer items-center justify-center px-3 py-3">
        <Checkbox
          checked={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={value => table.toggleAllRowsSelected(!!value)}
          aria-label={t('authenticated.onboarding.team:employeesTable.selectAll')}
        />
      </label>
    ),
    cell: ({ row }) => (
      <label className="mt-4 flex h-10 cursor-pointer items-center justify-center px-3">
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          aria-label={t('authenticated.onboarding.team:employeesTable.selectRow')}
        />
      </label>
    ),
    meta: { cellClassName: '!p-0 !align-top' },
    enableSorting: false,
    enableColumnFilter: false,
    size: 40,
    minSize: 40,
    maxSize: 40,
  }),
  columnHelper.display({
    id: 'name',
    header: t('authenticated.onboarding.team:employeesTable.name'),
    cell: ({ row }) => {
      const formIndex = getFormIndex(row.original.id);
      return (
        <FormField
          control={form.control}
          name={`employees.${formIndex}.name`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} value={field.value ?? ''} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    },
    meta: { cellClassName: '!align-top' },
    size: 300,
    minSize: 192,
  }),
  columnHelper.accessor('email', {
    header: t('authenticated.onboarding.team:employeesTable.email'),
    cell: ({ row }) => {
      const formIndex = getFormIndex(row.original.id);
      const isCurrentSuperAdminEmail =
        deps?.isCurrentUserSuperAdmin &&
        row.original.email?.trim().toLowerCase() === deps.currentUserEmail?.trim().toLowerCase();

      const isEmailDisabled =
        isCurrentSuperAdminEmail ||
        row.original.invitationStatus === TeamMemberInvitationStatusDTO.SENT ||
        row.original.invitationStatus === TeamMemberInvitationStatusDTO.ACCEPTED;

      return (
        <FormField
          control={form.control}
          name={`employees.${formIndex}.email`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input {...field} value={field.value ?? ''} disabled={isEmailDisabled} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    },
    meta: { cellClassName: '!align-top' },
    size: 400,
    minSize: 300,
  }),
  ...getIntegrationColumns(deps?.connectedIntegrations ?? []),
];
